import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	camelToSnake,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';
import { MediaType } from 'src/app/media/domain/media.domain';

import { DcBaseRequester } from '../core/dc-base.requester';
import { IMedia, IMediaResponse } from './media.model';

@Injectable({
	providedIn: 'root',
})
export class MediaRequester extends DcBaseRequester<IMediaResponse> {
	protected resource: string;
	protected elementsListUrl: string;
	protected singleElementUrl: string;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('MEDIAS_GET_ALL');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getMediaList(
		includePublic: boolean,
		useCache = true
	): Observable<ReadonlyArray<IMediaResponse>> {
		const resourceUrl = this.generateResourceUrl('MEDIA_GET_ALL_DETAILED');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				excludePublic: !includePublic,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					evictTimeoutInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					evictTimeoutInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IMediaResponse>>(url, cachePolicy);
	}

	public getMedia(mediaId: number, type: MediaType): Observable<IMedia> {
		const resourceUrl = this.generateResourceUrl(this.getApiKeyByType(type));
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: mediaId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IMedia>(url);
	}

	public changeActivationStatusMedia(media: IMedia): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			this.getApiKeyByType(media.type)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: media.id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {
			metadata: {
				active: media.metadata.actif,
				[camelToSnake('shortLabel')]: media.metadata.label,
			},
		});
	}

	public deleteMedia(mediaId: number, type: MediaType): Observable<void> {
		const resourceUrl = this.generateResourceUrl(this.getApiKeyByType(type));
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: mediaId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}

	public deleteManyMedias(mediaIds: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('MEDIAS_DELETE_BULK');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				mediasId: mediaIds,
			}
		);
		return this.http.delete<void>(url);
	}

	public duplicateMedia(mediaId: number, type: MediaType): Observable<number> {
		const resourceUrl = this.generateResourceUrl(
			this.getDuplicateApiKeyBYType(type)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: mediaId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.post<number>(url, {});
	}

	private getApiKeyByType(type: MediaType): string {
		if (type === 'image') {
			return 'MEDIAS_IMAGE_GET';
		}
		if (type === 'pdf') {
			return 'MEDIAS_PDF_GET';
		}
		throw new Error(`Unrecognized type: ${type}`);
	}

	private getDuplicateApiKeyBYType(type: MediaType): string {
		if (type === 'image') {
			return 'MEDIAS_IMAGE_DUPLICATE';
		}
		if (type === 'pdf') {
			return 'MEDIAS_PDF_DUPLICATE';
		}
		throw new Error(`Unrecognized type: ${type}`);
	}
}
